import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const MODULE_LABELS = {
  friend: "A Friend's Wisdom",
  psychologist: "A Moment of Clarity",
  mentor: "Words of Guidance",
  social_guide: "Social Insight",
  job_guardian: "Career Wisdom",
  life_coach: "Life Lesson",
  storyteller: "A Story's Moral",
};

const MODULE_COLORS = {
  friend: "from-pink-400/20 to-rose-300/10",
  psychologist: "from-blue-400/20 to-sky-300/10",
  mentor: "from-amber-400/20 to-yellow-300/10",
  social_guide: "from-green-400/20 to-emerald-300/10",
  job_guardian: "from-purple-400/20 to-violet-300/10",
  life_coach: "from-orange-400/20 to-amber-300/10",
  storyteller: "from-teal-400/20 to-cyan-300/10",
};

export default function InsightSharePage() {
  const { insightId } = useParams();
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchInsight();
  }, [insightId]);

  const fetchInsight = async () => {
    try {
      const { data } = await axios.get(`${API}/insights/${insightId}`);
      setInsight(data);
    } catch (err) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
        <Sparkles className="w-12 h-12 text-muted-foreground/30 mb-4" />
        <h1 className="font-heading text-2xl font-medium mb-2">Insight not found</h1>
        <p className="text-muted-foreground mb-6">This shared wisdom may have been removed.</p>
        <Link to="/">
          <Button className="btn-omni bg-primary text-primary-foreground">Discover Omni</Button>
        </Link>
      </div>
    );
  }

  const label = MODULE_LABELS[insight.module] || "Insight";
  const gradient = MODULE_COLORS[insight.module] || "from-primary/20 to-primary/5";

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-10">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-primary" strokeWidth={1.5} />
        </div>
        <span className="font-heading text-xl font-medium">Omni</span>
      </div>

      {/* Card */}
      <div data-testid="public-insight-card" className={`w-full max-w-md bg-gradient-to-br ${gradient} bg-card border border-border/50 rounded-[2rem] p-8 shadow-xl`}>
        <div className="flex items-center gap-2 mb-6">
          <Sparkles className="w-5 h-5 text-primary" strokeWidth={1.5} />
          <span className="text-sm font-medium text-primary">{label}</span>
        </div>

        <blockquote data-testid="insight-content" className="font-heading text-xl md:text-2xl font-medium leading-relaxed mb-8 text-foreground">
          "{insight.content}"
        </blockquote>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Sparkles className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-sm font-heading">Omni</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {insight.views > 1 ? `${insight.views} views` : "Shared with love"}
          </p>
        </div>
      </div>

      {/* CTA */}
      <div className="mt-10 text-center">
        <p className="text-muted-foreground mb-4">
          My AI friend Omni told me this. What do you think?
        </p>
        <Link to="/auth">
          <Button data-testid="share-page-cta" className="btn-omni bg-primary text-primary-foreground gap-2">
            Get your own Omni <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
        <p className="text-xs text-muted-foreground mt-4">Free forever. No email required.</p>
      </div>

      {/* Footer */}
      <footer className="mt-16 text-xs text-muted-foreground text-center">
        Omni is an AI companion. Not a licensed professional.
      </footer>
    </div>
  );
}
